import React, { useCallback, useEffect, useState } from 'react';
import { loadItemDetail } from '../../lib/orgCatalog';
import { useAuth } from '../../components/AuthContext';
import { useToast } from '../../components/ToastContext';
import { day } from '../../lib/when';
import { COMPANY_GROUPS, makeOurs, saveCompanyFields, addNumber, changeNumber, removeNumber } from './itemEdit';
import { Section as HouseSection } from '../admin/Ordering/panelParts';

/*
  ONE ITEM, OPENED, under the Item Catalog list. Job 37, 11 October 2026.

  **Every field of his list of 2 October (decision 6), in his groups, from 12
  October**, after he opened an item and found *"it is missing all of the fields
  we had picked and setup up previously"*. The first version hid any field with
  nothing in it, and for MedEx that was nearly all of them, so they looked
  missing. An empty field now says "not set".

  **What can be changed here**, his word of 12 October ("yes build the on-item
  editing next"): the company's own fields (Edit our details) and the ClaimSoft
  and Account Edge numbers. The vendor's fields are shared by every company and
  are shown, not edited.
*/

const SYSTEM = { claimsoft: 'ClaimSoft', account_edge: 'Account Edge' };
const HOW = {
  item_number: 'matched on the item number', ae_vendor_item_number: 'matched on the vendor item number',
  manual: 'matched by hand', vendor_part_number: 'matched on the vendor part number',
};
const money = n => (n == null ? '' : '$' + Number(n).toFixed(2));
const NOT_SET = <span style={{ color: 'var(--text-secondary)' }}>not set</span>;

function show(f, v) {
  if (f.kind === 'tick') return v ? 'Yes' : 'No';
  if (v == null || v === '') return NOT_SET;
  return f.kind === 'money' ? money(v) : String(v);
}

function Fact({ label, children, wide }) {
  return (
    <div style={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
      <div className="fld-label">{label}</div>
      <div style={{ fontSize: 15, overflowWrap: 'anywhere' }}>{children == null || children === '' ? NOT_SET : children}</div>
    </div>
  );
}

/* A section of the item, in the house style, with who the fields belong to as
   its note (12 October 2026, when he found the panel cramped and unlike the
   rest of OnHand). */
function Section({ title, who, action, children }) {
  return <HouseSection title={title} note={who} right={action || null}>{children}</HouseSection>;
}

const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px 24px' };
const VENDOR_SAYS = 'From the vendor, the same for every company';
const OURS_SAYS = 'Ours alone';

/* The company fields, as a form. */
function OurDetailsForm({ org, onSaved, onCancel }) {
  const [form, setForm] = useState(() => {
    const f = { our_description: org.our_description ?? '', notes: org.notes ?? '' };
    for (const g of COMPANY_GROUPS) for (const x of g.fields) f[x.key] = x.kind === 'tick' ? !!org[x.key] : (org[x.key] ?? '');
    return f;
  });
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  const set = k => v => setForm(x => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true); setProblem(null);
    const r = await saveCompanyFields(org.id, form);
    setSaving(false);
    if (!r.ok) { setProblem(r.error.message); return; }
    onSaved();
  };
  const input = (f) => f.kind === 'tick' ? (
    <label key={f.key} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 15 }}>
      <input type="checkbox" checked={!!form[f.key]} onChange={e => set(f.key)(e.target.checked)} /> {f.label}
    </label>
  ) : (
    <label key={f.key} style={{ display: 'block' }}>
      <span className="fld-label">{f.label}</span>
      <input className="fld" style={{ width: '100%' }} value={form[f.key]} inputMode={f.kind === 'text' ? undefined : 'decimal'}
        placeholder={f.hint} onChange={e => set(f.key)(e.target.value)} />
    </label>
  );
  return (
    <div style={{ padding: '4px 0' }}>
      <label style={{ display: 'block', marginBottom: 12 }}>
        <span className="fld-label">Our description</span>
        <input className="fld" style={{ width: '100%' }} value={form.our_description} placeholder="Empty uses the vendor's"
          onChange={e => set('our_description')(e.target.value)} />
      </label>
      {COMPANY_GROUPS.map(g => (
        <div key={g.title} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>{g.title}</div>
          <div style={grid}>{g.fields.map(input)}</div>
        </div>
      ))}
      <label style={{ display: 'block' }}>
        <span className="fld-label">Notes</span>
        <textarea className="fld" rows={3} style={{ width: '100%' }} value={form.notes} onChange={e => set('notes')(e.target.value)} />
      </label>
      {problem && <p style={{ color: 'var(--critical-ink)', margin: '10px 0 0' }}>{problem}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className="btn btn-quiet" disabled={saving} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
}

/* One number, with Change and Remove. */
function NumberRow({ n, catalogItemId, readOnly, userId, onDone }) {
  const [mode, setMode] = useState(null);   // 'change' | 'remove' | null
  const [text, setText] = useState(n.number);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const run = async fn => {
    setBusy(true); setProblem(null);
    const r = await fn();
    setBusy(false);
    if (!r.ok) { setProblem(r.error.message); return; }
    setMode(null);
    onDone(r);
  };
  const role = n.is_main ? 'main' : n.reason === 'medicare_billing' ? 'Medicare number' : 'other number';
  return (
    <li style={{ margin: '6px 0' }}>
      {mode === 'change' ? (
        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {SYSTEM[n.system]} ({role})
          <input className="fld" value={text} autoFocus onChange={e => setText(e.target.value)} aria-label={'New ' + SYSTEM[n.system] + ' number'} />
          <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => { setMode(null); setText(n.number); setProblem(null); }}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy || !text.trim() || text.trim() === n.number}
            onClick={() => run(() => changeNumber({ numberId: n.id, catalogItemId, system: n.system, number: text, userId }))}>
            {busy ? 'Saving...' : 'Save'}
          </button>
        </span>
      ) : (
        <span>
          {SYSTEM[n.system]} <b className="rpnum">{n.number}</b>
          <span style={{ color: 'var(--text-secondary)' }}>
            {' '}{role}{HOW[n.match_method] ? ', ' + HOW[n.match_method] : ''}{n.match_method === 'manual' && n.confirmed_at ? ' on ' + day(n.confirmed_at) : ''}
          </span>
          {!readOnly && mode === null && (
            <>
              <button type="button" className="btn btn-quiet" style={{ marginLeft: 8 }} onClick={() => setMode('change')}>Change</button>
              <button type="button" className="btn btn-quiet" onClick={() => setMode('remove')}>Remove</button>
            </>
          )}
          {mode === 'remove' && (
            <span style={{ marginLeft: 8, display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              Remove it from this item?
              <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setMode(null)}>Keep it</button>
              <button type="button" className="btn btn-danger" disabled={busy}
                onClick={() => run(() => removeNumber({ numberId: n.id, catalogItemId }))}>{busy ? 'Removing...' : 'Yes, remove it'}</button>
            </span>
          )}
        </span>
      )}
      {problem && <div style={{ color: 'var(--critical-ink)', fontSize: 13, marginTop: 4 }}>{problem}</div>}
    </li>
  );
}

/* Adding a number. */
function AddNumber({ catalogItemId, userId, onDone, onCancel }) {
  const [system, setSystem] = useState('claimsoft');
  const [text, setText] = useState('');
  const [medicare, setMedicare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const save = async () => {
    setBusy(true); setProblem(null);
    const r = await addNumber({ catalogItemId, system, number: text, medicare: system === 'claimsoft' && medicare, userId });
    setBusy(false);
    if (!r.ok) { setProblem(r.error.message); return; }
    onDone(r);
  };
  return (
    <div style={{ paddingTop: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="fld" value={system} onChange={e => setSystem(e.target.value)} aria-label="Which system">
          <option value="claimsoft">ClaimSoft</option>
          <option value="account_edge">Account Edge</option>
        </select>
        <input className="fld" value={text} autoFocus placeholder="The number" onChange={e => setText(e.target.value)} aria-label="The number" />
        {system === 'claimsoft' && (
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={medicare} onChange={e => setMedicare(e.target.checked)} /> It is the Medicare number
          </label>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary" disabled={busy || !text.trim()} onClick={save}>{busy ? 'Saving...' : 'Add it'}</button>
      </div>
      {problem && <div style={{ color: 'var(--critical-ink)', fontSize: 13, marginTop: 6 }}>{problem}</div>}
    </div>
  );
}

export default function ItemPanel({ catalogItemId, onClose, onChanged, readOnly = false }) {
  const { user } = useAuth();
  const toast = useToast();
  const userId = user && user.id;
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [making, setMaking] = useState(false);

  const read = useCallback(async () => {
    const r = await loadItemDetail(catalogItemId);
    setD(r);
  }, [catalogItemId]);
  useEffect(() => { setD(null); read(); }, [read]);

  /* After any number change: say what happened to the jobs, then read again. */
  const numbersChanged = r => {
    setAdding(false);
    const bits = ['Saved'];
    if (r.lines) bits.push('filled in on ' + r.lines + ' job line' + (r.lines === 1 ? '' : 's'));
    toast.success(bits.join(', ') + (r.nowReady && r.nowReady.length ? '. ' + r.nowReady.join(', ') + ' ready now.' : '.'));
    if (r.backToNeedsYou && r.backToNeedsYou.length) toast.error(r.backToNeedsYou.join(', ') + ' went back to Needs you.');
    if (r.warning) toast.error(r.warning);
    read();
    onChanged && onChanged();
  };

  const shell = (title, sub, body) => (
    <section className="card ua-detail" aria-label={title} style={{ marginTop: 16 }}>
      <div className="ua-detail-body">
        <div className="ua-detail-head">
          <div style={{ minWidth: 0 }}>
            <div className="ua-detail-name">{title}</div>
            {sub && <div className="ua-detail-sub" style={{ fontSize: 14 }}>{sub}</div>}
          </div>
          <button type="button" className="btn btn-quiet" onClick={onClose} aria-label="Close this item">Close</button>
        </div>
        {body}
      </div>
    </section>
  );

  if (!d) return shell('Opening...', null, <span style={{ color: 'var(--text-secondary)' }}>Reading the item...</span>);
  if (d.error && !d.item) return shell('This item could not be opened', null, <span style={{ color: 'var(--critical-ink)' }}>{d.error.message}</span>);

  const it = d.item;
  const org = d.orgItem;
  const order = { claimsoft: 0, account_edge: 1 };
  const numbers = [...d.numbers].sort((a, b) => order[a.system] - order[b.system] || (b.is_main ? 1 : 0) - (a.is_main ? 1 : 0));

  return shell(
    (org && org.our_description) || it.our_description || it.vendor_description || it.vendor_part_number,
    [it.oh_number, it.vendor ? it.vendor.name + ' ' + it.vendor_part_number : it.vendor_part_number].filter(Boolean).join(' \u00b7 '),
    <>
      {d.error && <p style={{ color: 'var(--critical-ink)', marginTop: 0 }}>Some of this item could not be read just now: {d.error.message}</p>}
      {it.needs_review && it.review_reason && (
        <p style={{ marginTop: 0, color: 'var(--warning-ink)', fontWeight: 700, fontSize: 13 }}>{it.review_reason}</p>
      )}

      <Section title="Our numbers for it" who={OURS_SAYS}
        action={!readOnly && !adding && <button type="button" className="btn btn-quiet" onClick={() => setAdding(true)}>+ Add a number</button>}>
        {numbers.length === 0 && !adding && (
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 13 }}>
            {org ? 'No ClaimSoft or Account Edge number yet.' : 'Not one of our items yet. Adding a number makes it one.'}
          </p>
        )}
        {numbers.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 15 }}>
            {numbers.map(n => <NumberRow key={n.id} n={n} catalogItemId={it.id} readOnly={readOnly} userId={userId} onDone={numbersChanged} />)}
          </ul>
        )}
        {adding && <AddNumber catalogItemId={it.id} userId={userId} onDone={numbersChanged} onCancel={() => setAdding(false)} />}
      </Section>

      <Section title="Our details" who={OURS_SAYS}
        action={!readOnly && !editing && (org
          ? <button type="button" className="btn btn-quiet" onClick={() => setEditing(true)}>Edit our details</button>
          : <button type="button" className="btn btn-quiet" disabled={making} onClick={async () => {
              setMaking(true);
              const r = await makeOurs(it.id);
              setMaking(false);
              if (!r.ok) { toast.error(r.error.message); return; }
              toast.success('It is one of our items now');
              await read(); onChanged && onChanged(); setEditing(true);
            }}>{making ? 'Saving...' : 'Make it one of our items'}</button>)}>
        {!org ? (
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 13 }}>Billing, prices and stock control are kept for our own items only.</p>
        ) : editing ? (
          <OurDetailsForm org={org} onCancel={() => setEditing(false)}
            onSaved={() => { setEditing(false); toast.success('Saved'); read(); onChanged && onChanged(); }} />
        ) : (
          <>
            <div style={grid}><Fact label="Our description" wide>{org.our_description}</Fact></div>
            {COMPANY_GROUPS.map(g => (
              <div key={g.title} style={{ marginTop: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 8 }}>{g.title}</div>
                <div style={grid}>{g.fields.map(f => <Fact key={f.key} label={f.label}>{show(f, org[f.key])}</Fact>)}</div>
              </div>
            ))}
            <div style={{ ...grid, marginTop: 14 }}>
              <Fact label="Our status">{org.status}</Fact>
              <Fact label="Notes" wide>{org.notes}</Fact>
            </div>
          </>
        )}
      </Section>

      <Section title="Who makes it" who={VENDOR_SAYS}>
        <div style={grid}>
          <Fact label="OH number">{it.oh_number}</Fact>
          <Fact label="Vendor">{it.vendor && it.vendor.name}</Fact>
          <Fact label="Vendor part number">{it.vendor_part_number}</Fact>
          <Fact label="Manufacturer">{it.manufacturer}</Fact>
          <Fact label="Brand">{it.brand}</Fact>
          <Fact label="Vendor's description" wide>{it.vendor_description}</Fact>
        </div>
      </Section>

      <Section title="What it is" who={VENDOR_SAYS}>
        <div style={grid}>
          <Fact label="Category">{it.category}</Fact>
          <Fact label="Product family">{it.product_family}</Fact>
          <Fact label="Size">{it.size}</Fact>
          <Fact label="Side">{it.side}</Fact>
          <Fact label="Gender">{it.gender}</Fact>
          <Fact label="Colour">{it.colour}</Fact>
        </div>
      </Section>

      <Section title="How it is packaged" who={VENDOR_SAYS}>
        {d.packs.length === 0 ? NOT_SET : (
          <span style={{ fontSize: 15 }}>
            {d.packs.map(p => p.uom + (Number(p.items_per_uom) > 0 ? ' of ' + Number(p.items_per_uom) : '') + (p.is_purchase_default ? ' (bought this way)' : '')).join(', ')}
          </span>
        )}
      </Section>

      <Section title="Prices, newest first" who="From the price books">
        <div style={{ ...grid, marginBottom: 8 }}><Fact label="Vendor's list price">{it.list_price == null ? null : money(it.list_price)}</Fact></div>
        {d.prices.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 13 }}>No price book has this item.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 15 }}>
            {d.prices.map((p, i) => (
              <li key={i} style={{ margin: '3px 0' }}>
                <b>{money(p.cost_per_uom)}</b> {p.purchase_uom ? 'per ' + p.purchase_uom : <span style={{ color: 'var(--warning-ink)' }}>the book does not say per what</span>}
                {p.cost_per_item != null && p.purchase_uom && p.uom_key !== 'EA' ? <span style={{ color: 'var(--text-secondary)' }}> ({money(p.cost_per_item)} each)</span> : null}
                <span style={{ color: 'var(--text-secondary)' }}> {'\u00b7'} {p.book_label}{p.priced_on ? ', ' + day(p.priced_on) : ''}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Its life" who="Status from the vendor; replacements are ours">
        <div style={grid}>
          <Fact label="Vendor status">{it.status}</Fact>
          <Fact label="Discontinued">{it.discontinued_on ? day(it.discontinued_on) : null}</Fact>
        </div>
        {d.conversions.length > 0 && (
          <ul style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 15 }}>
            {d.conversions.map((c, i) => (
              <li key={i}><span className="rpnum">{c.old_number}</span> {c.new_number ? <>is replaced by <span className="rpnum">{c.new_number}</span></> : 'is do not use, no replacement'}</li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Barcodes" who={VENDOR_SAYS}>
        {d.barcodes.length === 0 ? NOT_SET : <span className="rpnum" style={{ fontSize: 15 }}>{d.barcodes.map(b => b.code).join(', ')}</span>}
      </Section>
    </>
  );
}
